import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import { useAuth } from './AuthContext';

const StompContext = createContext();

export const useStomp = () => {
  const context = useContext(StompContext);
  if (!context) {
    throw new Error('useStomp must be used within a StompProvider');
  }
  return context;
};

export const StompProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const subscriptionsRef = useRef(new Map());
  const messageHandlersRef = useRef(new Map());

  // Debug logging
  useEffect(() => {
    console.log('StompProvider: Auth state changed');
    console.log('StompProvider: isAuthenticated:', isAuthenticated);
    console.log('StompProvider: user:', user);

    if (user) {
      console.log('StompProvider: user.area_id:', user.area_id);
      console.log('StompProvider: user.stomp_key:', user.stomp_key);
    }
  }, [isAuthenticated, user]);

  // Initialize STOMP connection when user is authenticated
  useEffect(() => {
    console.log('StompProvider: useEffect triggered');
    console.log('StompProvider: isAuthenticated:', isAuthenticated);
    console.log('StompProvider: user exists:', !!user);

    if (isAuthenticated && user) {
      console.log('StompProvider: Conditions met, calling connectStomp');
      connectStomp();
    } else {
      console.log('StompProvider: Conditions not met, calling disconnectStomp');
      disconnectStomp();
    }

    return () => {
      console.log('StompProvider: Cleanup, disconnecting');
      disconnectStomp();
    };
  }, [isAuthenticated, user]);

  const subscribeToTimelineTopic = (visibilityParams) => {
    if (!clientRef.current?.connected || !user) {
      console.log('STOMP: Cannot subscribe to timeline - client connected:', clientRef.current?.connected, 'user:', !!user);
      return;
    }

    // Unsubscribe from existing timeline topics
    const existingTopics = Array.from(subscriptionsRef.current.keys())
      .filter(topic => topic.includes('timeline_') || topic.includes('city_timeline_') || topic.includes('state_timeline_'));

    existingTopics.forEach(topic => {
      const subscription = subscriptionsRef.current.get(topic);
      if (subscription) {
        subscription.unsubscribe();
        subscriptionsRef.current.delete(topic);
        console.log('STOMP: Unsubscribed from:', topic);
      }
    });

    // Determine new timeline topic based on visibility
    let timelineTopic;
    if (visibilityParams.city_id) {
      timelineTopic = `/topic/area_q/city_timeline_${visibilityParams.city_id}`;
    } else if (visibilityParams.state_id) {
      timelineTopic = `/topic/area_q/state_timeline_${visibilityParams.state_id}`;
    } else if (visibilityParams.area_id) {
      timelineTopic = `/topic/area_q/timeline_${visibilityParams.area_id}`;
    } else {
      // Default to user's area
      const areaId = user.area_id || user.area?._id?.['$oid'] || user.area?._id;
      timelineTopic = `/topic/area_q/timeline_${areaId}`;
    }

    // Subscribe to new timeline topic
    try {
      console.log('STOMP: Subscribing to timeline topic:', timelineTopic);
      const subscription = clientRef.current.subscribe(timelineTopic, handleMessage);
      subscriptionsRef.current.set(timelineTopic, subscription);
      console.log('STOMP: Successfully subscribed to timeline:', timelineTopic);
    } catch (error) {
      console.error('STOMP: Failed to subscribe to timeline topic:', error);
    }
  };

  const connectStomp = () => {
    console.log('StompProvider: connectStomp called');

    if (clientRef.current?.connected) {
      console.log('StompProvider: Already connected, skipping');
      return;
    }

    if (!user) {
      console.log('StompProvider: No user, cannot connect');
      return;
    }

    const currentLoc = window.location.href;
    let hostname = window.location.hostname;

    // For development, connect directly to ActiveMQ WebSocket port
    let url = `wss://${window.location.host}/ws`;
    // if (currentLoc.indexOf('localhost') !== -1 || currentLoc.indexOf('dev') !== -1) {
      // url = 'ws://localhost:61614/';
    // } else {
     // url = `wss://${window.location.host}/ws`;
    // }

    console.log('StompProvider: Connecting to', url);
    console.log('StompProvider: Hostname:', hostname);
    setConnectionStatus('connecting');

    const client = new Client({
      brokerURL: url,
      connectHeaders: {
        login: 'guest',
        passcode: 'guest',
        host: hostname,
        saslMechanism: 'ANONYMOUS'
      },
      debug: function(str) {
        console.log('STOMP DEBUG:', str);
      },
      heartbeatIncoming: 18000,
      heartbeatOutgoing: 18000,
      reconnectDelay: 1000,
      onConnect: (frame) => {
        console.log('STOMP: Got connect: ' + JSON.stringify(frame));
        console.log('STOMP: Connection established, about to subscribe to topics');
        setIsConnected(true);
        setConnectionStatus('connected');

        // Subscribe to all topics like the old ProfileWidget
        subscribeToAllTopics();

        // Log all active subscriptions after a brief delay
        setTimeout(() => {
          console.log('STOMP: Active subscriptions after connect:');
          console.log('STOMP: Total subscriptions:', subscriptionsRef.current.size);
          console.log('STOMP: Subscribed topics:', Array.from(subscriptionsRef.current.keys()));
        }, 500);
      },
      onStompError: (frame) => {
        console.error('STOMP Error:', frame);
        setIsConnected(false);
        setConnectionStatus('error');
      },
      onWebSocketError: (event) => {
        console.error('WebSocket Error:', event);
        setIsConnected(false);
        setConnectionStatus('error');
      },
      onDisconnect: () => {
        console.log('STOMP: Disconnected');
        setIsConnected(false);
        setConnectionStatus('disconnected');
      }
    });

    console.log('StompProvider: Client created, activating...');
    clientRef.current = client;
    client.activate();
  };

  const disconnectStomp = () => {
    if (clientRef.current) {
      console.log('STOMP: Disconnecting');
      clientRef.current.deactivate();
      clientRef.current = null;
      setIsConnected(false);
      setConnectionStatus('disconnected');
      subscriptionsRef.current.clear();
      messageHandlersRef.current.clear();
    }
  };

  const subscribeToAllTopics = () => {
    console.log('StompProvider: subscribeToAllTopics called');
    console.log('StompProvider: user:', user);
    console.log('StompProvider: client connected:', clientRef.current?.connected);

    if (!user || !clientRef.current?.connected) {
      console.log('StompProvider: Cannot subscribe - missing user or connection');
      return;
    }

    // Check user object structure
    console.log('StompProvider: user.area_id type:', typeof user.area_id);
    console.log('StompProvider: user.area_id value:', user.area_id);
    console.log('StompProvider: user.stomp_key:', user.stomp_key);

    // Handle different area_id formats
    let areaId = user.area_id;
    if (typeof areaId === 'object' && areaId['$oid']) {
      areaId = areaId['$oid'];
    }

    console.log('StompProvider: Processed area_id:', areaId);

    // Exact same topics as the old ProfileWidget
    const stompTopics = [
      `/topic/chat_q/user_${user.stomp_key}`,
      `/topic/notification_q/user_${user.stomp_key}`,
      `/topic/posts_q/area_${areaId}`, // For post updates, deletions, and poll responses
    ];

    console.log('STOMP: Subscribing to base topics:', stompTopics);

    stompTopics.forEach(topic => {
      try {
        console.log('STOMP: Subscribing to:', topic);
        const subscription = clientRef.current.subscribe(topic, handleMessage);
        subscriptionsRef.current.set(topic, subscription);
        console.log('STOMP: Successfully subscribed to:', topic);
      } catch (error) {
        console.error('STOMP: Error subscribing to', topic, error);
      }
    });

    // Subscribe to default timeline topic (user's area)
    const userAreaId = user.area_id || user.area?._id?.['$oid'] || user.area?._id;
    console.log('STOMP: User area_id for default timeline subscription:', userAreaId);
    subscribeToTimelineTopic({});

    console.log('STOMP: Total subscriptions:', subscriptionsRef.current.size);
    console.log('STOMP: Subscribed topics:', Array.from(subscriptionsRef.current.keys()));
  };

  // Main message handler - matches old ProfileWidget logic exactly
  const handleMessage = (msg) => {
    console.log('STOMP: Raw message received:', msg);

    let message;
    try {
      message = JSON.parse(msg.body);
    } catch (error) {
      console.error('STOMP: Error parsing message:', error);
      return;
    }

    const topic = msg.headers.destination;

    console.log('STOMP: Parsed message on', topic, message);
    console.log('STOMP: Message structure analysis:');
    console.log('  - message.message exists:', !!message.message);
    console.log('  - message.notification exists:', !!message.notification);
    console.log('  - message.vote_update exists:', !!message.vote_update);
    console.log('  - message.post exists:', !!message.post);
    console.log('  - message.user exists:', !!message.user);

    // Route messages based on content type (updated for new message format)
    if (message.message) {
      // Chat message
      console.log('STOMP: Routing as chat message');
      handleChatMessage(message, topic);
    } else if (message.vote_update) {
      // Vote update on timeline (simplified format)
      console.log('STOMP: Routing as vote update');
      handleVoteUpdate(message, topic);
    } else if (message.notification && message.notification.vote) {
      // Vote notification (legacy format)
      console.log('STOMP: Routing as vote message (legacy format)');
      handleVoteMessage(message, topic);
    } else if (message.notification && message.notification.comment) {
      // Comment notification (new format)
      console.log('STOMP: Routing as comment message (new format)');
      handleCommentMessage(message, topic);
    } else if (message.comment_update) {
      // Comment update on timeline (new format)
      console.log('STOMP: Routing as comment update');
      handleCommentUpdate(message, topic);
    } else if (message.comment_delete) {
      // Comment deletion on timeline (new format)
      console.log('STOMP: Routing as comment delete');
      handleCommentDelete(message, topic);
    } else if (message.notification && message.notification.poll_response_count) {
      // Poll response notification
      console.log('STOMP: Routing as poll response');
      handlePollResponse(message, topic);
      // Also handle as activity notification
      handleActivityNotification(message, topic);
    } else if (message.notification) {
      // General activity notification (votes, comments, etc.)
      console.log('STOMP: Routing as activity notification');
      handleActivityNotification(message, topic);
    } else if (message.type === 'poll_update') {
      // Poll update message
      console.log('STOMP: Routing as poll update');
      handleTimelinePost(message, topic);
    } else if (message.post && message.user) {
      // Timeline post - this is what we need for real-time posts!
      console.log('STOMP: Routing as timeline post');
      handleTimelinePost(message, topic);
    } else {
      // Unknown message type
      console.log('STOMP: Unknown message type:', JSON.stringify(message));
      console.log('STOMP: This might be a timeline post with unexpected structure');
    }

    // Also notify any registered handlers for this specific topic
    const handlers = messageHandlersRef.current.get(topic) || [];
    console.log('STOMP: Notifying', handlers.length, 'topic handlers for', topic);
    handlers.forEach(handler => {
      try {
        handler(message, topic);
      } catch (error) {
        console.error('STOMP: Error in message handler:', error);
      }
    });

    // Also notify global handlers
    const globalHandlers = messageHandlersRef.current.get('*') || [];
    console.log('STOMP: Notifying', globalHandlers.length, 'global handlers');
    globalHandlers.forEach(handler => {
      try {
        handler(message, topic);
      } catch (error) {
        console.error('STOMP: Error in global message handler:', error);
      }
    });
  };

  // Individual message type handlers
  const handleChatMessage = (message, topic) => {
    console.log('STOMP: Chat message received:', message);
    // Notify chat handlers
    const chatHandlers = messageHandlersRef.current.get('chat') || [];
    console.log('STOMP: Notifying', chatHandlers.length, 'chat handlers');
    chatHandlers.forEach(handler => handler(message, topic));
  };

  const handleVoteMessage = (message, topic) => {
    console.log('STOMP: Vote message received:', message);
    // Notify vote handlers
    const voteHandlers = messageHandlersRef.current.get('vote') || [];
    console.log('STOMP: Notifying', voteHandlers.length, 'vote handlers');
    voteHandlers.forEach(handler => handler(message, topic));
  };

  const handleVoteUpdate = (message, topic) => {
    console.log('STOMP: Vote update received on topic:', topic);
    console.log('STOMP: Vote update message:', message);
    console.log('STOMP: Vote update details:', {
      postId: message.vote_update?.post?._id,
      commentId: message.vote_update?.comment?._id,
      upvotes: message.vote_update?.upvotes,
      downvotes: message.vote_update?.downvotes,
      direction: message.vote_update?.new_direction,
      voter: message.vote_update?.voter?.username
    });
    // Notify vote update handlers (for timeline vote activity)
    const voteHandlers = messageHandlersRef.current.get('vote') || [];
    console.log('STOMP: Notifying', voteHandlers.length, 'vote handlers for update');
    voteHandlers.forEach(handler => handler(message, topic));
  };

  const handleCommentMessage = (message, topic) => {
    console.log('STOMP: Comment message received:', message);
    // Notify comment handlers
    const commentHandlers = messageHandlersRef.current.get('comment') || [];
    console.log('STOMP: Notifying', commentHandlers.length, 'comment handlers');
    commentHandlers.forEach(handler => handler(message, topic));
  };

  const handleCommentUpdate = (message, topic) => {
    console.log('STOMP: Comment update received:', message);
    // Notify comment update handlers (for timeline comment activity)
    const commentHandlers = messageHandlersRef.current.get('comment') || [];
    console.log('STOMP: Notifying', commentHandlers.length, 'comment handlers for update');
    commentHandlers.forEach(handler => handler(message, topic));
  };

  const handleCommentDelete = (message, topic) => {
    console.log('STOMP: Comment delete received:', message);
    // Notify comment delete handlers (for timeline comment activity)
    const commentHandlers = messageHandlersRef.current.get('comment') || [];
    console.log('STOMP: Notifying', commentHandlers.length, 'comment handlers for delete');
    commentHandlers.forEach(handler => handler(message, topic));
  };

  const handlePollResponse = (message, topic) => {
    console.log('STOMP: Poll response received:', message);
    // Notify poll handlers
    const pollHandlers = messageHandlersRef.current.get('poll') || [];
    console.log('STOMP: Notifying', pollHandlers.length, 'poll handlers');
    pollHandlers.forEach(handler => handler(message, topic));
  };

  const handleTimelinePost = (message, topic) => {
    console.log('STOMP: Timeline post received:', message.post?.title || 'Unknown title');
    console.log('STOMP: Full message structure:', JSON.stringify(message, null, 2));
    console.log('STOMP: Topic:', topic);

    // Notify timeline handlers
    const timelineHandlers = messageHandlersRef.current.get('timeline') || [];
    console.log('STOMP: Notifying', timelineHandlers.length, 'timeline handlers');

    if (timelineHandlers.length === 0) {
      console.warn('STOMP: No timeline handlers registered!');
    }

    timelineHandlers.forEach((handler, index) => {
      try {
        console.log(`STOMP: Calling timeline handler ${index + 1}`);
        handler(message, topic);
        console.log(`STOMP: Timeline handler ${index + 1} completed successfully`);
      } catch (error) {
        console.error(`STOMP: Error in timeline handler ${index + 1}:`, error);
      }
    });
  };

  const handleActivityNotification = (message, topic) => {
    console.log('STOMP: Activity notification received:', message.notification);

    // Notify activity notification handlers
    const activityHandlers = messageHandlersRef.current.get('activity') || [];
    console.log('STOMP: Notifying', activityHandlers.length, 'activity notification handlers');

    activityHandlers.forEach((handler, index) => {
      try {
        console.log(`STOMP: Calling activity handler ${index + 1}`);
        handler(message, topic);
        console.log(`STOMP: Activity handler ${index + 1} completed successfully`);
      } catch (error) {
        console.error(`STOMP: Error in activity handler ${index + 1}:`, error);
      }
    });
  };

  const subscribe = (topic, callback) => {
    if (!clientRef.current?.connected) {
      console.warn('STOMP: Cannot subscribe, not connected');
      return null;
    }

    console.log('STOMP: Manual subscription to', topic);

    const subscription = clientRef.current.subscribe(topic, callback);
    subscriptionsRef.current.set(topic, subscription);

    return subscription;
  };

  const unsubscribe = (topic) => {
    const subscription = subscriptionsRef.current.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      subscriptionsRef.current.delete(topic);
      console.log('STOMP: Unsubscribed from', topic);
    }
  };

  // Add handler for specific message types
  const addMessageHandler = (type, handler) => {
    const handlers = messageHandlersRef.current.get(type) || [];
    handlers.push(handler);
    messageHandlersRef.current.set(type, handlers);

    console.log(`STOMP: Added ${type} handler, total handlers:`, handlers.length);

    // Return cleanup function
    return () => {
      const currentHandlers = messageHandlersRef.current.get(type) || [];
      const index = currentHandlers.indexOf(handler);
      if (index > -1) {
        currentHandlers.splice(index, 1);
        messageHandlersRef.current.set(type, currentHandlers);
        console.log(`STOMP: Removed ${type} handler`);
      }
    };
  };

  const removeMessageHandler = (type, handler) => {
    const handlers = messageHandlersRef.current.get(type) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      messageHandlersRef.current.set(type, handlers);
    }
  };

  const publish = (topic, message) => {
    if (!clientRef.current?.connected) {
      console.warn('STOMP: Cannot publish, not connected');
      return false;
    }

    try {
      clientRef.current.publish({
        destination: topic,
        body: typeof message === 'string' ? message : JSON.stringify(message)
      });
      console.log('STOMP: Published to', topic);
      return true;
    } catch (error) {
      console.error('STOMP: Publish error', error);
      return false;
    }
  };

  const value = {
    isConnected,
    connectionStatus,
    subscribe,
    unsubscribe,
    publish,
    addMessageHandler,
    removeMessageHandler,
    reconnect: connectStomp,
    subscribeToTimelineTopic,
    // Expose user info for topic construction
    user
  };

  return (
    <StompContext.Provider value={value}>
      {children}
    </StompContext.Provider>
  );
};
